import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalProps } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { ModalBodyTableFromToProps } from '@ui/components/Modal/Body/Table/FromTo'

interface Props<T> extends ModalBodyTableFromToProps<T> {
  title: string
  description?: string
  onConfirm: (from: T, to: T) => void
}

type FromToModalCompleteProps<T> = BaseModalProps & Props<T>

export const FromToModal = <T = string,>(props: FromToModalCompleteProps<T>) => {
  const { close, description, from, to, onConfirm } = props
  const [formFrom, setFormFrom] = useState<T | null>(from?.value ?? null)
  const [formTo, setFormTo] = useState<T | null>(to?.value ?? null)
  const { t } = useTranslation()
  const { title } = props

  const handleConfirm = () => {
    if (!formFrom || !formTo) return
    onConfirm(formFrom, formTo)
    close()
  }

  const handleFromChange = (value: string | null) => {
    setFormFrom((typeof from?.value === 'number' ? Number(value) : value) as T)
  }

  const handleToChange = (value: string | null) => {
    setFormTo((typeof to?.value === 'number' ? Number(value) : value) as T)
  }

  return (
    <Modal.FullScreen>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Table.FromTo<T>
          {...props}
          from={{
            ...from,
            value: formFrom,
            onChange: from?.onChange ? from.onChange : handleFromChange,
          }}
          to={{
            ...to,
            value: formTo,
            onChange: to?.onChange ? to.onChange : handleToChange,
          }}
        />
        {description && <Modal.Body.Text>{description}</Modal.Body.Text>}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary disabled={!formFrom || !formTo} onClick={handleConfirm}>
          {t('Apply')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}
